import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Heart, 
  Sparkles, 
  CheckCircle2, 
  Users, 
  Leaf, 
  Compass, 
  Target, 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight,
  Sun,
  Activity,
  TreePine,
  HelpCircle,
  ChevronDown
} from 'lucide-react';
import useDocumentMeta from '../hooks/useDocumentMeta';

export default function Section103() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useDocumentMeta(
    'Paragraf 103 i Brørup | Beskæftigelse med dyr | Kontakt os nu',
    'Paragraf 103 i Brørup, oplev tryg beskyttet beskæftigelse med dyr, natur og støtte tilpasset dig. Kontakt os om rundvisning og pladser.',
    ''
  );

  const targetAudiencePoints = [
    'Udviklingsforstyrrelser og kognitive udfordringer, herunder udfordringer inden for autismespektret',
    'Erhvervet hjerneskade og behov for særligt tilpassede arbejdsrammer',
    'Nedsat fysisk eller psykisk funktionsevne med behov for støtte og struktur',
    'Særlige sociale udfordringer, der gør ordinær beskæftigelse vanskelig',
  ];

  const workActivities = [
    {
      title: 'Dyrepleje og daglige rutiner',
      desc: 'Dyrepleje og daglige rutiner omkring dyrene skaber omsorg, ro og ansvarsfølelse.',
      icon: Heart,
    },
    {
      title: 'Havearbejde og grønne områder',
      desc: 'Havearbejde og vedligeholdelse af grønne områder med fysisk aktivitet i frisk luft.',
      icon: TreePine,
    },
    {
      title: 'Naturbaserede aktiviteter',
      desc: 'Naturbaserede og udendørs aktiviteter tilpasset borgerens dagsform og ressourcer.',
      icon: Leaf,
    },
    {
      title: 'Kreative værkstedsopgaver',
      desc: 'Kreative og praktiske værkstedsopgaver med fokus på fordybelse og succesoplevelser.',
      icon: Sparkles,
    },
    {
      title: 'Fællesskab og pauser',
      desc: 'Fælles pauser, oplevelser og sociale aktiviteter, der styrker trivsel og relationer.',
      icon: Users,
    },
  ];

  const faqItems = [
    {
      question: "Hvad betyder serviceloven 103 i hverdagen?",
      answer: "Serviceloven 103, også omtalt som paragraf 103 eller § 103, handler om beskyttet beskæftigelse for personer under folkepensionsalderen, som ikke kan få eller fastholde arbejde på normale vilkår. Hos LIVSLYST foregår det som en overskuelig arbejdsdag med støtte, nærvær og praktiske opgaver tilpasset den enkelte."
    },
    {
      question: "Hvem kan få beskyttet beskæftigelse 103?",
      answer: "Beskyttet beskæftigelse 103 kan være relevant for personer med betydeligt nedsat fysisk eller psykisk funktionsevne, kognitive udfordringer, udviklingsforstyrrelser, erhvervet hjerneskade eller særlige sociale udfordringer. En diagnose giver ikke automatisk adgang. Kommunen vurderer funktionsevne, arbejdsmuligheder og støttebehov, mens LIVSLYST undersøger, om tilbuddet passer til borgeren."
    },
    {
      question: "Hvordan får man bevilget et tilbud efter § 103?",
      answer: "Første skridt er at kontakte sagsbehandleren i bopælskommunen. Man kan ikke selv tilmelde sig et tilbud efter § 103, da kommunen skal foretage en konkret vurdering og træffe afgørelse om bevillingen. LIVSLYST kan efterfølgende bidrage til dialogen om borgerens behov, interesser og det mulige match."
    },
    {
      question: "Hvilke arbejdsopgaver kan man have i beskyttet beskæftigelse med dyr og natur?",
      answer: "Hos LIVSLYST kan arbejdsdagen blandt andet indeholde dyrepleje, daglige rutiner omkring dyrene, havearbejde, vedligeholdelse af grønne områder, naturbaserede aktiviteter samt kreative og praktiske værkstedsopgaver. Opgaverne tilpasses borgerens ressourcer, interesser, dagsform og behov for støtte."
    },
    {
      question: "Hvordan tilpasses arbejdsdagen ved nedsat funktionsevne?",
      answer: "LIVSLYST skaber en forudsigelig arbejdsdag med faste rutiner, visuel støtte og tydelig kommunikation. Ved nedsat funktionsevne tilpasses opgaver, krav, tempo og støtte, så borgeren kan deltage i trygge rammer og få realistiske succesoplevelser uden unødigt pres."
    },
    {
      question: "Hvilken pædagogisk støtte gives i beskyttet beskæftigelse?",
      answer: "LIVSLYST arbejder med anerkendende relationer, medbestemmelse og respekt for den enkeltes grænser. Den pædagogiske støtte kan blandt andet omfatte Low Arousal, jegstøttende samtaler og sansemotoriske aktiviteter, som skal forebygge stress og gøre kommunikation, opgaver og forventninger lettere at forstå."
    },
    {
      question: "Hvordan følges trivsel og udvikling i et § 103-forløb?",
      answer: "Hos LIVSLYST følges udviklingen gennem individuelle mål, handleplaner, trivselssamtaler og løbende evaluering. Der kan anvendes konkrete SMART-mål, når det passer til borgeren. Det giver et fagligt grundlag for at justere støtten og tale med kommunen om trivsel og progression."
    },
    {
      question: "Kan man få en rundvisning og høre om aktuelle pladser?",
      answer: "Ja. Borgere, pårørende og fagpersoner kan kontakte LIVSLYST for en uforpligtende samtale, en rundvisning og information om aktuelle pladser. Tilbuddet har plads til seks borgere, men en rundvisning er ikke det samme som en bevilling, da det altid er bopælskommunen, der træffer afgørelse om et forløb."
    }
  ];

  const faqSchema = {
    "@type": "FAQPage",
    "@context": "https://schema.org",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const supportMethods = [
    {
      title: 'Struktur & forudsigelighed',
      desc: 'Faste rutiner, visuel støtte og klar kommunikation skaber tryghed i hverdagen.',
      icon: Compass,
    },
    {
      title: 'Det lille fællesskab',
      desc: 'Maksimalt 6 borgere sikrer høj faglighed, genkendelighed og tæt individuelt nærvær.',
      icon: Users,
    },
    {
      title: 'Low Arousal & trivsel',
      desc: 'Vi forebygger stress gennem rolige relationer, tilpassede krav og anerkendende dialog.',
      icon: Shield,
    },
    {
      title: 'Mål & progression',
      desc: 'Individuelle handleplaner, trivselssamtaler og SMART-mål, der følger udviklingen.',
      icon: Target,
    },
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      
      {/* 1. HERO / HEADER SECTION */}
      <section className="bg-gradient-to-b from-stone-100 to-stone-50 py-16 md:py-24 border-b border-stone-200 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 border border-emerald-300 text-emerald-900 text-xs font-semibold uppercase tracking-wider"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-600 animate-pulse"></span>
            Servicelovens § 103
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight"
          >
            Paragraf 103, beskyttet beskæftigelse med nærvær i Brørup
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed font-normal"
          >
            Hos <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST</Link> tilbyder vi privat beskyttet beskæftigelse efter servicelovens § 103. I vores rolige rammer i Brørup får den enkelte en overskuelig arbejdsdag med støtte, nærvær og praktiske opgaver. Vi har plads til seks borgere, så der er gode muligheder for at tilpasse hverdagen til den enkelte.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-2 flex flex-wrap justify-center gap-4 text-sm"
          >
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl shadow-xs transition-colors"
            >
              <span>Kontakt os om pladser</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a 
              href="tel:51504654"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors"
            >
              <Phone className="w-4 h-4 text-emerald-700" />
              <span>Ring på 51 50 46 54</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. SECTION: HVAD BETYDER SERVICELOVEN 103 I HVERDAGEN? */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content */}
            <motion.div 
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Shield className="w-4 h-4" />
                <span>Lovgivning & Rammer</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Hvad betyder serviceloven 103 i hverdagen?
              </h2>

              <p className="text-stone-600 leading-relaxed text-base sm:text-lg">
                Paragraf 103 i serviceloven betyder, at kommunen skal tilbyde beskyttet beskæftigelse til personer under folkepensionsalderen, som på grund af betydeligt nedsat fysisk eller psykisk funktionsevne eller særlige sociale problemer ikke kan få eller fastholde arbejde på normale vilkår.
              </p>

              <p className="text-stone-600 leading-relaxed text-base">
                Kommunen foretager en konkret vurdering af borgerens behov og muligheder efter anden lovgivning, før et forløb kan bevilges. Du kan derfor ikke selv tilmelde dig et tilbud efter § 103. Din sagsbehandler i bopælskommunen er indgangen til visitation, mens vi gerne bidrager til dialogen om et muligt match.
              </p>

              <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 flex items-start gap-4">
                <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-800 shrink-0">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 text-sm sm:text-base">
                    Uforpligtende dialog og rådgivning
                  </h4>
                  <p className="text-stone-600 text-xs sm:text-sm mt-1 leading-relaxed">
                    Vi inviterer altid gerne borgere, pårørende og sagsbehandlere på besøg for at se rammerne og tale om et eventuelt forløb.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right Visual Image */}
            <motion.div 
              initial={{ opacity: 0, x: 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-lg border border-stone-200 bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/c5f66cf8-78de-45ab-990a-d94928372a1d.jpeg"
                  alt="Trygge og nærværende rammer i beskyttet beskæftigelse"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent flex items-end p-6">
                  <div className="text-white">
                    <span className="text-xs uppercase tracking-wider font-semibold text-emerald-300">
                      LIVSLYST i Brørup
                    </span>
                    <p className="text-sm font-medium text-stone-100 mt-1">
                      Maksimalt 6 borgere – ro, struktur og faglig støtte
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 3. SECTION: HVEM KAN FÅ BESKYTTET BESKÆFTIGELSE 103? */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <Users className="w-4 h-4" />
              <span>Målgruppebeskrivelse</span>
            </div>
            
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Hvem kan få beskyttet beskæftigelse 103?
            </h3>
            
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Betegnelsen beskyttet beskæftigelse 103 bruges om arbejdsrettede tilbud til voksne, som har brug for andre rammer end på det ordinære arbejdsmarked. En diagnose giver ikke i sig selv adgang. Kommunen vurderer funktionsevne, arbejdsmuligheder og støttebehov, mens vi undersøger, om vores rammer passer til borgeren.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {targetAudiencePoints.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-stone-200 shadow-xs hover:border-emerald-300 transition-colors flex items-start gap-4"
              >
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700 shrink-0 mt-0.5">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-medium">
                  {item}
                </p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. SECTION: MENINGSFULD BESKÆFTIGELSE & ARBEJDSOPGAVER MED DYR OG NATUR */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
              <Leaf className="w-4 h-4" />
              <span>Praksis & Hverdag</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              Hvordan bliver beskyttet beskæftigelse meningsfuld?
            </h2>
            
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Meningsfuld beskæftigelse opstår, når opgaverne er virkelige, forståelige og tilpasset den person, der skal udføre dem. Dyr og natur er en integreret del af vores miljø. Her kan faste rutiner og konkrete gøremål være med til at skabe struktur, ansvarsfølelse og oplevelsen af at bidrage.
            </p>

            <p className="text-stone-600 text-base leading-relaxed">
              Vi planlægger opgaverne med udgangspunkt i borgerens ressourcer, interesser, dagsform og behov for støtte. Nogle trives med genkendelige rutiner, mens andre har glæde af variation. Krav og tempo justeres, så deltagelse og realistiske succesoplevelser får plads uden at presse den enkelte.
            </p>
          </div>

          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                Arbejdsopgaver med dyr og natur, der kan tilpasses
              </h3>
              <p className="text-stone-600 text-sm sm:text-base max-w-2xl mx-auto mt-2 leading-relaxed">
                Hverdagen kan rumme både selvstændige opgaver og aktiviteter med støtte fra en medarbejder. Opgaverne vælges ud fra det aktuelle match og kan ændre sig over tid. Det betyder, at borgeren kan øve færdigheder og ansvar i et tempo, der opleves trygt og overskueligt.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
              {workActivities.map((activity, idx) => {
                const IconComponent = activity.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                    className="bg-stone-50 p-6 rounded-2xl border border-stone-200 hover:shadow-sm hover:border-emerald-300 transition-all flex flex-col justify-between"
                  >
                    <div className="space-y-4">
                      <div className="p-3 bg-emerald-100/70 text-emerald-800 rounded-xl w-fit">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <h4 className="text-lg font-bold text-stone-900">
                        {activity.title}
                      </h4>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        {activity.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}

              {/* Extra visual summary card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="bg-gradient-to-br from-emerald-800 to-emerald-950 text-white p-6 rounded-2xl shadow-sm flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <span className="text-xs uppercase tracking-wider text-emerald-300 font-bold">
                    Fleksibel hverdag
                  </span>
                  <h4 className="text-lg font-bold text-white">
                    Afstemt efter borgerens dagsform
                  </h4>
                  <p className="text-emerald-100/90 text-sm leading-relaxed">
                    Opgaverne og tempoet tilpasses altid, så borgeren oplever succes, ro og overskud i hverdagen.
                  </p>
                </div>
                <Link 
                  to="/beskaeftigelsen" 
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-200 hover:text-white pt-4 transition-colors"
                >
                  <span>Læs mere om vores hverdagsaktiviteter</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            </div>
          </div>

        </div>
      </section>

      {/* 5. SECTION: STØTTE VED NEDSAT FUNKTIONSEVNE & TRYG UDVIKLING */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Image on left for variation */}
            <motion.div 
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5 order-2 lg:order-1"
            >
              <div className="rounded-3xl overflow-hidden shadow-md border border-stone-200 bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/52713da9-b8ad-4644-95bb-94e117047ffb.jpeg"
                  alt="Dyrepleje og nærvær i trygge omgivelser"
                  className="w-full h-80 sm:h-[450px] object-cover"
                />
              </div>
            </motion.div>

            {/* Content on right */}
            <motion.div 
              initial={{ opacity: 0, x: 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 space-y-6 order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Heart className="w-4 h-4" />
                <span>Pædagogisk Støtte</span>
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Støtte ved nedsat funktionsevne, som tager udgangspunkt i dig
              </h2>

              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Når du lever med nedsat funktionsevne, kan tydelighed og forudsigelighed have stor betydning for, om en arbejdsdag bliver overskuelig. Vi arbejder med faste rutiner, visuel støtte og klar kommunikation. Støtten tilpasses, så borgeren ved, hvad der skal ske, og hvem der kan hjælpe.
              </p>

              <p className="text-stone-600 text-base leading-relaxed">
                Det lille fællesskab giver mulighed for genkendelighed og tæt individuelt nærvær. Vi lægger vægt på anerkendende relationer, medbestemmelse og respekt for den enkeltes grænser. Målet er at styrke trivsel, selvværd, sociale færdigheder og størst mulig selvstændighed i hverdagen.
              </p>

              <div className="pt-4 space-y-4 border-t border-stone-200">
                <h3 className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight">
                  Tryg udvikling med realistiske mål
                </h3>
                
                <p className="text-stone-600 text-base leading-relaxed">
                  Vi anvender blandt andet Low Arousal, jegstøttende samtaler og sansemotoriske aktiviteter som en del af den pædagogiske støtte. Det betyder, at vi søger at forebygge unødig stress gennem rolige relationer, tilpassede krav og en kommunikation, som er let at forstå.
                </p>

                <p className="text-stone-600 text-base leading-relaxed">
                  Udviklingen følges gennem individuelle mål, handleplaner, trivselssamtaler og løbende evaluering. Vi kan arbejde med konkrete SMART mål, når det passer til borgeren. På den måde får vi et fagligt grundlag for at justere indsatsen og tale med kommunen om trivsel og progression.
                </p>
              </div>
            </motion.div>

          </div>

          {/* 4 Feature highlight blocks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {supportMethods.map((method, idx) => {
              const Icon = method.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-stone-200 shadow-xs"
                >
                  <div className="p-2.5 bg-emerald-50 text-emerald-800 rounded-xl w-fit mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-stone-900 text-base mb-1.5">{method.title}</h4>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">{method.desc}</p>
                </motion.div>
              );
            })}
          </div>

        </div>
      </section>

      {/* 5B. SECTION: OFTE STILLEDE SPØRGSMÅL (FAQ) - 2 KOLONNER */}
      <section className="py-16 md:py-24 bg-stone-100/70 border-b border-stone-200 relative overflow-hidden">
        {/* Schema.org FAQPage structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100/80 text-emerald-800 text-xs font-bold uppercase tracking-wider border border-emerald-200/60 shadow-2xs">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>Ofte stillede spørgsmål</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Spørgsmål & svar om § 103
            </h2>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Her finder du svar på de mest almindelige spørgsmål om servicelovens § 103, visitation, arbejdsopgaver og vores tilpassede pædagogiske rammer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-start">
            {/* Venstre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(0, Math.ceil(faqItems.length / 2)).map((item, index) => {
                const actualIndex = index;
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white/90 border-stone-200/90 hover:border-stone-300 hover:bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600 group-hover:bg-stone-200'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Højre kolonne */}
            <div className="space-y-3 sm:space-y-4">
              {faqItems.slice(Math.ceil(faqItems.length / 2)).map((item, idx) => {
                const actualIndex = idx + Math.ceil(faqItems.length / 2);
                const isOpen = openFaqIndex === actualIndex;
                return (
                  <motion.div
                    key={actualIndex}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (idx + 4) * 0.05 }}
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'bg-white border-emerald-300/80 shadow-sm ring-1 ring-emerald-200/50'
                        : 'bg-white/90 border-stone-200/90 hover:border-stone-300 hover:bg-white'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : actualIndex)}
                      className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
                      aria-expanded={isOpen}
                    >
                      <span className="text-base sm:text-lg font-semibold text-stone-900 leading-snug">
                        {item.question}
                      </span>
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                          isOpen
                            ? 'bg-emerald-800 text-white rotate-180'
                            : 'bg-stone-100 text-stone-600 group-hover:bg-stone-200'
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>

                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="px-5 sm:px-6 pb-5 pt-1 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100"
                      >
                        <p>{item.answer}</p>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 6. SECTION: SÅDAN KOMMER DU VIDERE & KONTAKT I BRØRUP */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="bg-stone-50 border border-stone-200 rounded-3xl p-8 sm:p-12 shadow-sm space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <Target className="w-4 h-4" />
                <span>Visitation & Match</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
                Sådan kommer du videre med § 103
              </h2>

              <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                Første skridt er at tale med din sagsbehandler om, hvorvidt du er i målgruppen for serviceloven 103. Hvis kommunen vurderer, at paragraf 103 er relevant, kan vi tage en dialog om behov, interesser, støtte og rammer. Her er fokus på at finde et reelt match frem for en løsning, der skal passe til alle.
              </p>

              <p className="text-stone-600 text-base leading-relaxed">
                Som borger, pårørende eller fagperson er du også velkommen til at spørge os om hverdagen og om aktuelle pladser. Vi kan fortælle mere om vores beskæftigelsestilbud og de praktiske opgaver, men det er altid bopælskommunen, der træffer afgørelse om bevilling til § 103.
              </p>
            </div>

            <div className="pt-6 border-t border-stone-200 space-y-4">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
                <MapPin className="w-4 h-4" />
                <span>Geografi & Besøg</span>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight">
                Kontakt os og oplev rammerne i Brørup
              </h3>

              <p className="text-stone-600 text-base leading-relaxed">
                Du finder LIVSLYST på Surhavevej 14 A i Brørup. Placeringen kan være relevant for borgere og kommuner i blandt andet Vejen, Esbjerg og Kolding, men selve tilbuddet ligger i Brørup. Kontakt os gerne for en uforpligtende samtale eller en rundvisning, så vi sammen kan afklare, om rammerne er det rette match.
              </p>

              {/* Action Buttons & Contact Box */}
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link 
                  to="/kontakt"
                  className="flex items-center justify-center gap-2 p-4 bg-emerald-700 hover:bg-emerald-800 text-white font-medium rounded-xl transition-colors shadow-xs"
                >
                  <Mail className="w-4 h-4" />
                  <span>Send en besked</span>
                </Link>

                <a 
                  href="tel:51504654"
                  className="flex items-center justify-center gap-2 p-4 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-xl transition-colors shadow-xs"
                >
                  <Phone className="w-4 h-4 text-emerald-400" />
                  <span>Ring 51 50 46 54</span>
                </a>

                <a 
                  href="https://maps.google.com/?q=Surhavevej+14A+6650+Brørup"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 p-4 bg-white hover:bg-stone-100 text-stone-800 font-medium rounded-xl border border-stone-300 transition-colors"
                >
                  <MapPin className="w-4 h-4 text-emerald-700" />
                  <span>Find vej i Brørup</span>
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>

    </div>
  );
}
